import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../hooks/usePermission.js';

import { AIChatPanel } from './AIChatPanel.js';
import { VoiceCall } from './VoiceCall.js';

/**
 * Titlebar button that toggles the AI assistant panel — lives beside the
 * Home / POS shortcuts. Renders nothing for users without `ai.use`.
 */
export function AILauncherButton({ open, onToggle }: { open: boolean; onToggle: () => void }): JSX.Element | null {
  const { t } = useTranslation('ai');
  const canUse = usePermission('ai.use');
  if (!canUse) return null;
  return (
    <button
      type="button"
      className="icon-btn"
      onClick={onToggle}
      aria-label={t('open')}
      title={t('open')}
      aria-pressed={open}
      style={open ? { color: 'var(--color-brand)' } : undefined}
    >
      <span className="text-base" aria-hidden>✨</span>
    </button>
  );
}

/**
 * The AI assistant surface (chat panel + voice call), toggled by the titlebar
 * button. Renders nothing for users without `ai.use`. Keyboard shortcut
 * Ctrl/⌘+K (or `?` from anywhere outside a text input) toggles/opens the panel.
 */
export function AILauncher({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }): JSX.Element | null {
  const canUse = usePermission('ai.use');

  useEffect(() => {
    if (!canUse) return;
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      const inTextField =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      } else if (e.key === '?' && !inTextField) {
        e.preventDefault();
        onOpenChange(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canUse, open, onOpenChange]);

  if (!canUse) return null;

  return (
    <>
      <VoiceCall />
      <AIChatPanel open={open} onClose={() => onOpenChange(false)} />
    </>
  );
}
